// Auto-layout utilities for Flowise

/**
 * Auto-layout nodes in a hierarchical tree structure
 * @param {Array} nodes - Array of node objects
 * @param {Array} connections - Array of connection objects
 * @param {Object} options - Layout options
 * @returns {Array} - Nodes with updated x, y positions
 */
export const autoLayout = (nodes, connections, options = {}) => {
  const {
    direction = 'TB', // TB (top-bottom), LR (left-right)
    nodeSpacingX = 200,
    nodeSpacingY = 120,
    startX = 100,
    startY = 100
  } = options;

  if (nodes.length === 0) return [];

  // Build adjacency list
  const children = {};
  const parents = {};
  
  nodes.forEach(node => {
    children[node.id] = [];
    parents[node.id] = [];
  });

  connections.forEach(conn => {
    if (children[conn.from] && parents[conn.to]) {
      children[conn.from].push(conn.to);
      parents[conn.to].push(conn.from);
    }
  });

  // Find root nodes (no incoming connections)
  const roots = nodes.filter(node => parents[node.id].length === 0);
  
  // If no roots found (circular), use first node
  if (roots.length === 0 && nodes.length > 0) {
    roots.push(nodes[0]);
  }

  // Assign levels using BFS
  const levels = {};
  const visited = new Set();
  const queue = [];

  roots.forEach(root => {
    levels[root.id] = 0;
    queue.push(root.id);
    visited.add(root.id);
  });

  while (queue.length > 0) {
    const nodeId = queue.shift();
    const nodeChildren = children[nodeId] || [];
    
    nodeChildren.forEach(childId => {
      if (!visited.has(childId)) {
        visited.add(childId);
        levels[childId] = (levels[nodeId] || 0) + 1;
        queue.push(childId);
      }
    });
  }

  // Handle unvisited nodes (disconnected)
  nodes.forEach(node => {
    if (!visited.has(node.id)) {
      levels[node.id] = 0;
    }
  });

  // Group nodes by level
  const nodesByLevel = {};
  nodes.forEach(node => {
    const level = levels[node.id] || 0;
    if (!nodesByLevel[level]) {
      nodesByLevel[level] = [];
    }
    nodesByLevel[level].push(node);
  });

  // Calculate positions
  const maxLevel = Math.max(...Object.keys(nodesByLevel).map(Number));
  const updatedNodes = nodes.map(node => {
    const level = levels[node.id] || 0;
    const nodesAtLevel = nodesByLevel[level];
    const indexAtLevel = nodesAtLevel.findIndex(n => n.id === node.id);
    const countAtLevel = nodesAtLevel.length;

    // Center nodes at each level
    const totalWidth = (countAtLevel - 1) * nodeSpacingX;
    const levelStartX = startX + (direction === 'TB' ? -totalWidth / 2 + 400 : 0);
    const levelStartY = startY + (direction === 'LR' ? -totalWidth / 2 + 300 : 0);

    let x, y;
    if (direction === 'TB') {
      x = levelStartX + indexAtLevel * nodeSpacingX;
      y = startY + level * nodeSpacingY;
    } else {
      x = startX + level * nodeSpacingX;
      y = levelStartY + indexAtLevel * nodeSpacingY;
    }

    return {
      ...node,
      x: Math.round(x / 20) * 20, // Snap to grid
      y: Math.round(y / 20) * 20
    };
  });

  return updatedNodes;
};

/**
 * Align selected nodes
 * @param {Array} nodes - Selected nodes
 * @param {string} alignment - 'left', 'center', 'right', 'top', 'middle', 'bottom'
 * @returns {Array} - Nodes with updated positions
 */
export const alignNodes = (nodes, alignment) => {
  if (nodes.length < 2) return nodes;

  const bounds = {
    minX: Math.min(...nodes.map(n => n.x)),
    maxX: Math.max(...nodes.map(n => n.x + n.width)),
    minY: Math.min(...nodes.map(n => n.y)),
    maxY: Math.max(...nodes.map(n => n.y + n.height))
  };

  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  return nodes.map(node => {
    let x = node.x;
    let y = node.y;

    switch (alignment) {
      case 'left':
        x = bounds.minX;
        break;
      case 'center':
        x = centerX - node.width / 2;
        break;
      case 'right':
        x = bounds.maxX - node.width;
        break;
      case 'top':
        y = bounds.minY;
        break;
      case 'middle':
        y = centerY - node.height / 2;
        break;
      case 'bottom':
        y = bounds.maxY - node.height;
        break;
      default:
        break;
    }

    return {
      ...node,
      x: Math.round(x / 20) * 20,
      y: Math.round(y / 20) * 20
    };
  });
};

/**
 * Distribute nodes evenly
 * @param {Array} nodes - Selected nodes
 * @param {string} direction - 'horizontal' or 'vertical'
 * @returns {Array} - Nodes with updated positions
 */
export const distributeNodes = (nodes, direction) => {
  if (nodes.length < 3) return nodes;

  const sorted = [...nodes].sort((a, b) => 
    direction === 'horizontal' ? a.x - b.x : a.y - b.y
  );

  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  if (direction === 'horizontal') {
    const totalSpace = (last.x + last.width) - first.x;
    const nodeWidths = sorted.reduce((sum, n) => sum + n.width, 0);
    const gap = (totalSpace - nodeWidths) / (sorted.length - 1);

    let currentX = first.x;
    return sorted.map((node, i) => {
      const newX = i === 0 ? node.x : currentX;
      currentX = newX + node.width + gap;
      return { ...node, x: Math.round(newX / 20) * 20 };
    });
  } else {
    const totalSpace = (last.y + last.height) - first.y;
    const nodeHeights = sorted.reduce((sum, n) => sum + n.height, 0);
    const gap = (totalSpace - nodeHeights) / (sorted.length - 1);

    let currentY = first.y;
    return sorted.map((node, i) => {
      const newY = i === 0 ? node.y : currentY;
      currentY = newY + node.height + gap;
      return { ...node, y: Math.round(newY / 20) * 20 };
    });
  }
};

export default { autoLayout, alignNodes, distributeNodes };
